import ko from 'knockout';
import moment from 'moment';
import numeral from 'numeral';
import 'moment-duration-format';
import { customFetch as fetch } from '../fetch';
import { resolveCdnUrl } from '../cdn-url-resolver';
import orderBy from 'lodash.orderby';

export class ClocksSalaryCostsModel {
    currentProjectedSalaryCosts: KnockoutComputed<number>;
    totalProjectedSalaryCosts: KnockoutComputed<number>;
    actualSalaryCosts: KnockoutComputed<number>;
    formattedCurrentProjectedCost: KnockoutComputed<string>;
    formattedTotalProjectedCost: KnockoutComputed<string>;
    formattedActualCost: KnockoutComputed<string>;
    formattedSavings: KnockoutComputed<string>;

    dummyObservable: KnockoutObservable<any>;

    constructor(parentModel: ClocksModel) {
        this.dummyObservable = ko.observable<any>();

        this.currentProjectedSalaryCosts = ko.pureComputed(() => {
            this.dummyObservable();

            return parentModel.allFilteredEntries()
                .filter(clock => moment().isAfter(clock.startTime()))
                .reduce((a: number, b: ClockEntryModel) => {
                    let duration = moment.duration(moment().diff(b.startTime())).asHours();
                    const totalDuration = moment.duration(b.endTime().diff(b.startTime())).asHours();

                    if (duration > totalDuration) {
                        duration = totalDuration;
                    }

                    return a + (duration * b.hourlyRate());
                }, 0);
        });

        this.totalProjectedSalaryCosts = ko.pureComputed(() => {
            this.dummyObservable();

            return parentModel.allFilteredEntries()
                .reduce((a: number, b: ClockEntryModel) => {
                    const duration = moment.duration(b.endTime().diff(b.startTime())).asHours();

                    return a + (duration * b.hourlyRate());
                }, 0);
        });

        this.formattedCurrentProjectedCost = ko.pureComputed(() => numeral(this.currentProjectedSalaryCosts()).format('$0,0.00'));

        this.formattedTotalProjectedCost = ko.pureComputed(() => numeral(this.totalProjectedSalaryCosts()).format('$0,0.00'));

        this.actualSalaryCosts = ko.pureComputed(() => {
            this.dummyObservable();

            return parentModel.allFilteredEntries()
                //.filter(clock => moment().isAfter(clock.startTime()))
                .reduce((a: number, b: ClockEntryModel) => {
                    const timeWorked = moment.duration(b.timeElapsedInMillis(EntryLineType.Working)).asHours();
                    return a + (timeWorked * b.hourlyRate());
                }, 0)
        });

        this.formattedActualCost = ko.pureComputed(() => numeral(this.actualSalaryCosts()).format('$0,0.00'));

        this.formattedSavings = ko.pureComputed(() => {
            const savedMoney = this.currentProjectedSalaryCosts() - this.actualSalaryCosts();
            return `${numeral(Math.abs(savedMoney)).format('$0,0.00')} ${savedMoney < 0 ? 'meer' : 'minder' } dan begroot`;
        });

        window.setInterval(() => this.dummyObservable.notifySubscribers(), 1000);
    }
}

export class ClocksModel {
    entries: KnockoutObservableArray<ClockEntryModel>;
    searchTerm: KnockoutObservable<string>;
    salaryCosts: KnockoutObservable<ClocksSalaryCostsModel>;

    filteredEntries: KnockoutComputed<ClockEntryModel[]>;
    allFilteredEntries: KnockoutComputed<ClockEntryModel[]>;

    constructor(model: any[], filterEmployeeType: KnockoutObservable<string>, filterDepartment: KnockoutObservable<string>) {
        let mappedEntries = model.map((r: any) => new ClockEntryModel(r));
        this.entries = ko.observableArray<ClockEntryModel>(mappedEntries);
        this.searchTerm = ko.observable<string>('');

        this.allFilteredEntries = ko.pureComputed(() => {
            return orderBy(ko.utils.arrayFilter(this.entries(), (item: ClockEntryModel) => {
                const normalizedSearchTerm = this.searchTerm().toLowerCase();
                const normalizedEmployeeName = item.employeeName().toLowerCase();

                let isSearchMatch = normalizedSearchTerm.length < 3
                    || normalizedEmployeeName.indexOf(normalizedSearchTerm) > -1;

                if (item.functionName()) {
                    isSearchMatch = isSearchMatch || item.functionName().toLowerCase().indexOf(normalizedSearchTerm) > -1;
                }

                if (filterEmployeeType()) {
                    isSearchMatch = isSearchMatch && item.employeeTypeId() === filterEmployeeType();
                }

                if (filterDepartment()){
                    isSearchMatch = isSearchMatch && item.departmentId() === filterDepartment();
                }

                return isSearchMatch && true;
            }), [(item) => item.employeeName()], ['asc']);
        });

        this.filteredEntries = ko.pureComputed(() => {
            return ko.utils.arrayFilter(this.allFilteredEntries(), (item: ClockEntryModel) => {
                return item.finished() === null;
            });
        });

        this.salaryCosts = ko.observable<ClocksSalaryCostsModel>(new ClocksSalaryCostsModel(this));
    }

    public remove(entry: ClockEntryModel) {
        this.entries.remove(entry);
    }
}

export class ClockEntryModel {
    employeeShiftId: KnockoutObservable<string>;
    employeeId: KnockoutObservable<string>;
    employeeTypeId: KnockoutObservable<string>;
    employeeName: KnockoutObservable<string>;
    functionName: KnockoutObservable<string>;
    eventName: KnockoutObservable<string>;
    departmentName: KnockoutObservable<string>;
    departmentId : KnockoutObservable<string>;
    branchName: KnockoutObservable<string>;
    hourlyRate: KnockoutObservable<number>;
    startTime: KnockoutObservable<moment.Moment>;
    endTime: KnockoutObservable<moment.Moment>;
    lines: KnockoutObservableArray<ClockEntryLineModel>;
    selectedGroup: KnockoutObservable<string>;
    finished: KnockoutObservable<moment.Moment>;

    formattedHourlyRate: KnockoutComputed<string>;

    avatarHash: KnockoutObservable<string>;
    avatarUrl: KnockoutComputed<string>;

    timeWorkingMillis: KnockoutObservable<number>;
    timeWorking: KnockoutComputed<string>;
    timeWorkingSeconds: KnockoutComputed<string>;
    timerWorking: KnockoutObservable<number>;

    timeBreakMillis: KnockoutObservable<number>;
    timeBreak: KnockoutComputed<string>;
    timeBreakSeconds: KnockoutComputed<string>;
    timerBreak: KnockoutObservable<number>;
    canViewProfile: KnockoutObservable<boolean>;

    constructor(model: any) {
        let mappedLines = model.lines.map((rrl: any) => new ClockEntryLineModel(rrl));
        this.lines = ko.observableArray<ClockEntryLineModel>(mappedLines);
        this.selectedGroup = ko.observable<string>(model.selectedGroup);

        this.employeeShiftId = ko.observable<string>(model.shiftId);
        this.employeeId = ko.observable<string>(model.employeeId);
        this.employeeTypeId = ko.observable<string>(model.employeeTypeId);
        this.functionName = ko.observable<string>(model.functionName);
        this.eventName = ko.observable<string>(model.eventName);
        this.branchName = ko.observable<string>(model.branchName);
        this.departmentName = ko.observable<string>(model.departmentName);
        this.departmentId = ko.observable<string>(model.departmentId);
        this.employeeName = ko.observable<string>(model.employeeName);
        this.startTime = ko.observable<moment.Moment>(moment(model.startTime));
        this.endTime = ko.observable<moment.Moment>(moment(model.endTime));
        this.avatarHash = ko.observable<string>(model.avatarHash);
        this.hourlyRate = ko.observable<number>(model.hourlyRate);
        this.finished = ko.observable<moment.Moment>(model.finished !== null ? moment(model.finished) : null);

        this.timerWorking = ko.observable<number>();
        this.timerBreak = ko.observable<number>();

        this.timeWorkingMillis = ko.observable<number>(this.timeElapsedInMillis(EntryLineType.Working));
        this.timeBreakMillis = ko.observable<number>(this.timeElapsedInMillis(EntryLineType.Break));
        this.canViewProfile = ko.observable<boolean>(model.canViewProfile);
        
        this.formattedHourlyRate = ko.pureComputed(() => numeral(this.hourlyRate()).format('$0,0.00'));

        this.timeWorking = ko.pureComputed(() => {
            return moment.duration(this.timeWorkingMillis(), 'ms').format('HH:mm', {
                trim: false,
                trunc: true
            });
        });

        this.timeBreak = ko.pureComputed(() => {
            return moment.duration(this.timeBreakMillis(), 'ms').format('HH:mm', {
                trim: false,
                trunc: true
            });
        });

        this.timeWorkingSeconds = ko.pureComputed(() => {
            return moment.duration(this.timeWorkingMillis(), 'ms').seconds().toString();
        });

        this.timeBreakSeconds = ko.pureComputed(() => {
            return moment.duration(this.timeBreakMillis(), 'ms').seconds().toString();
        });

        let lastLine = this.lastLine();

        if (lastLine !== null
            && typeof lastLine !== 'undefined') {
            if (lastLine.type() == EntryLineType.Break) {
                this.setBreakTimer();
            } else {
                this.setWorkingTimer();
            }
        }

        this.avatarUrl = ko.pureComputed(() => resolveCdnUrl(this.avatarHash(), {
            thumb: true,
            resize: '200x200%5E',
            gravity: 'center',
            crop: '200x200+0+50%',
            extent: '200x200',
            optimize: true,
            progressive: true
        }));
    }

    public async start(): Promise<void> {
        let lastLine = this.lastLine();
        if (lastLine !== null && typeof lastLine !== 'undefined') {
            lastLine.endDate(moment());
        }

        this.addLine(EntryLineType.Working);

        await fetch(`/Employer/Clocks/Start/${this.employeeShiftId()}`, {
            method: 'POST'
        });

        this.setWorkingTimer();
    }

    public async pause(): Promise<void> {
        let lastLine = this.lastLine();

        if (lastLine !== null && typeof lastLine !== 'undefined') {
            lastLine.endDate(moment());
        }
        this.addLine(EntryLineType.Break);

        await fetch(`/Employer/Clocks/Pause/${this.employeeShiftId()}`, {
            method: 'POST'
        });

        this.setBreakTimer();
    }

    public async finish(): Promise<void> {
        if (confirm('Weet u zeker dat u de dienst van deze medewerker wilt beëindigen?')) {
            let lastLine = this.lastLine();
            if (lastLine !== null && typeof lastLine !== 'undefined') {
                lastLine.endDate(moment());
            }

            await fetch(`/Employer/Clocks/Finish/${this.employeeShiftId()}`, {
                method: 'POST'
            });

            this.stopTimers();
            this.finished(moment());
        }
    }

    public shouldShowStartButton(): boolean {
        let lastLine = this.lastLine();
        return this.lines().length === 0 || (lastLine !== null
            && lastLine.type() === EntryLineType.Break);
    }

    private setWorkingTimer(): void {
        clearInterval(this.timerBreak());

        let timer = window.setInterval(() => {
            this.timeWorkingMillis(this.timeWorkingMillis() + 1000);
        }, 1000);

        this.timerWorking(timer);
    }

    private setBreakTimer(): void {
        clearInterval(this.timerWorking());

        let timer = window.setInterval(() => {
            this.timeBreakMillis(this.timeBreakMillis() + 1000);
        }, 1000);

        this.timerBreak(timer);
    }

    private stopTimers(): void {
        clearInterval(this.timerWorking());
        clearInterval(this.timerBreak());
    }

    private lastLine(): ClockEntryLineModel {
        let lastLine = this.lines()[this.lines().length - 1];
        return lastLine;
    }

    private addLine(type: EntryLineType): void {
        let newLineModel = {
            startDate: new Date(),
            endDate: undefined,
            type
        };

        let newLine = new ClockEntryLineModel(newLineModel);
        this.lines.push(newLine);
    }

    public timeElapsedInMillis(type: EntryLineType): number {
        return this.lines()
            .filter((l: ClockEntryLineModel) => l.type() == type)
            .reduce((value, entryLine) => {
                let endDate = entryLine.endDate() && entryLine.endDate().isValid()
                    ? entryLine.endDate()
                    : moment();

                let duration = endDate.valueOf() - entryLine.startDate().valueOf();

                return value += duration;
            }, 0);
    }
}

export class ClockEntryLineModel {
    startDate: KnockoutObservable<moment.Moment>;
    endDate: KnockoutObservable<moment.Moment>;
    type: KnockoutObservable<EntryLineType>;

    constructor(model: any) {
        this.startDate = ko.observable<moment.Moment>(moment(model.startDate));
        this.endDate = ko.observable<moment.Moment>();
        this.type = ko.observable<EntryLineType>(model.type);

        if (model.endDate) {
            this.endDate(moment(model.endDate));
        }
    }
}

export enum EntryLineType {
    Working,
    Break
}
